import { fnUrl } from "@/lib/btqApi";
/**
 * Traffic attribution + per-visitor ref code (BTQ-65/66 wave 2, 2026-08-06).
 * EB twin of lumen-studio-prod/src/lib/attribution.ts — same machinery, brand
 * constants differ (EB- prefix, cookie-based consent, EB WhatsApp text).
 *
 * WHAT IS CAPTURED
 * On the first hit of every VISIT (one capture per tab session) the module
 * snapshots where the visitor came from: utm_source/medium/campaign/content/term,
 * the ad click ids (gclid / gbraid / wbraid / fbclid / msclkid), document.referrer,
 * the landing path and a timestamp. The very first snapshot a visitor ever
 * produces is frozen forever (FIRST touch); the latest one is refreshed once per
 * visit (LAST touch). A 5-char ref code (EB-XXXXX, A-Z0-9) is generated once per
 * visitor and never changes.
 *
 * WHERE IT GOES
 * - trafficFields() is spread into both lead-form payloads. traffic_source +
 *   traffic_detail feed the CRM's «Доп. источники» string (live since 24.07,
 *   first-touch based, unchanged shape) — traffic_detail now ends with the ref
 *   code. The discrete fields lead_ref / gclid / utm_* / landing_page ride along
 *   for CRM columns.
 * - enrichContactHref() stitches the ref code into wa.me prefilled texts and
 *   mailto subjects at click time (called by analytics.ts); StickyWhatsApp builds
 *   its href through waHref() at render time.
 * - trafficType() feeds the traffic_type param on contact_click / generate_lead.
 *
 * FIRST vs LAST touch in the outputs (deliberate, don't "fix" one to the other):
 * traffic_source / traffic_detail = first touch (lead origin, CRM continuity);
 * gclid + utm_* = last touch when the current visit is tagged, else first touch
 * (the click id of the visit that converted is what offline-conversion import
 * needs); landing_page = the current visit's landing; traffic_type = last touch
 * (what brought THIS session).
 *
 * STORAGE (spec 06.08)
 * Before cookie-banner Accept: sessionStorage only — no persistent identifier
 * without consent, the record dies with the tab. After Accept: first-party
 * cookie, 90 days (plus the sessionStorage mirror). The pre-08/2026 first-touch
 * record in localStorage (bq_attribution_v1) is migrated in as the first touch;
 * the legacy key itself is left untouched.
 *
 * CONSENT COUPLING
 * The consent read + event name are duplicated from analytics.ts on purpose:
 * analytics.ts imports THIS module, and a back-import would be a cycle.
 */

const REF_PREFIX = "EB";
const STORE_KEY = "bq_attr_v2"; // cookie name AND sessionStorage key
const LEGACY_KEY = "bq_attribution_v1"; // pre-08/2026 record, read-only
const VISIT_MARK = "bq_attr_visit"; // sessionStorage: this visit already captured
const COOKIE_DAYS = 90;
const CONSENT_COOKIE = "cookie_consent"; // mirror of analytics.ts
const CONSENT_EVENT = "eb:consent"; // mirror of analytics.ts

/** One number for both brands — the shared BTQ/EB WhatsApp line. */
export const WHATSAPP_NUMBER = "351923272822";

/** Prefilled text used when a wa.me link carries no text of its own. */
const DEFAULT_WA_TEXT = "Hello! I'd like to plan an event in Portugal.";

export type TrafficType = "paid" | "organic" | "social" | "ai" | "referral" | "direct";

interface Touch {
  ts: string; // ISO timestamp of the visit's first hit
  landing: string; // pathname + search at visit start
  referrer?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  msclkid?: string;
}

interface AttributionV2 {
  v: 2;
  ref_code: string; // "EB-XXXXX"
  first: Touch; // never overwritten
  last: Touch; // refreshed once per visit
}

let cached: AttributionV2 | null = null;

/* ---------------------------------------------------------------- consent */

function consentGranted(): boolean {
  return readCookie(CONSENT_COOKIE) === "accepted";
}

/* ---------------------------------------------------------------- storage */

function readCookie(name: string): string | null {
  try {
    const m = document.cookie.match(new RegExp("(?:^|;\\s*)" + name + "=([^;]*)"));
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
}

function writeCookie(name: string, value: string, days: number): void {
  try {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax`;
  } catch {
    /* cookies unavailable */
  }
}

function parse(raw: string | null): AttributionV2 | null {
  if (!raw) return null;
  try {
    const a = JSON.parse(raw) as AttributionV2;
    return a && a.v === 2 && a.ref_code && a.first && a.last ? a : null;
  } catch {
    return null;
  }
}

function load(): AttributionV2 | null {
  const fromCookie = parse(readCookie(STORE_KEY));
  if (fromCookie) return fromCookie;
  try {
    return parse(sessionStorage.getItem(STORE_KEY));
  } catch {
    return null;
  }
}

function persist(a: AttributionV2): void {
  const json = JSON.stringify(a);
  try {
    sessionStorage.setItem(STORE_KEY, json);
  } catch {
    /* private mode */
  }
  if (!consentGranted()) return;
  // Cookie guard: a maximal record (10 params × 120 chars × 2 touches) encodes
  // to just under the 4096-byte RFC 6265 limit, and any multibyte character
  // tips it over — a truncated cookie fails JSON.parse and loses everything.
  // Over the threshold, persist a slimmed copy instead.
  let cookieJson = json;
  if (encodeURIComponent(json).length > 3500) {
    const slim = (t: Touch): Touch => {
      const out: Touch = { ts: t.ts, landing: t.landing.slice(0, 80) };
      if (t.referrer) out.referrer = t.referrer.slice(0, 80);
      for (const k of [...UTM_KEYS, ...CLICK_ID_KEYS]) {
        const v = t[k];
        if (v) out[k] = v.slice(0, 60);
      }
      return out;
    };
    cookieJson = JSON.stringify({ v: 2, ref_code: a.ref_code, first: slim(a.first), last: slim(a.last) });
  }
  writeCookie(STORE_KEY, cookieJson, COOKIE_DAYS);
}

/* ------------------------------------------------------------- capturing */

const CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid", "fbclid", "msclkid"] as const;
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

function currentTouch(): Touch {
  const params = new URLSearchParams(window.location.search);
  let refHost = "";
  try {
    refHost = document.referrer ? new URL(document.referrer).hostname : "";
  } catch {
    /* ignore */
  }
  // Ignore self-referrals (internal navigation after a hard reload)
  const externalReferrer = refHost && refHost !== window.location.hostname ? document.referrer : "";
  const touch: Touch = {
    ts: new Date().toISOString(),
    landing: (window.location.pathname + window.location.search).slice(0, 200),
  };
  if (externalReferrer) touch.referrer = externalReferrer.slice(0, 200);
  for (const key of [...UTM_KEYS, ...CLICK_ID_KEYS]) {
    const v = params.get(key);
    if (v) touch[key] = v.slice(0, 120);
  }
  return touch;
}

/** Seeds the first touch from the pre-08/2026 localStorage record, if present. */
function legacyFirstTouch(): Touch | null {
  try {
    const raw = localStorage.getItem(LEGACY_KEY);
    if (!raw) return null;
    const legacy = JSON.parse(raw) as Record<string, string | undefined>;
    const touch: Touch = {
      ts: legacy.first_seen || new Date().toISOString(),
      landing: (legacy.landing_page || "/").slice(0, 200),
    };
    if (legacy.referrer) touch.referrer = legacy.referrer.slice(0, 200);
    if (legacy.utm_source) touch.utm_source = legacy.utm_source;
    if (legacy.utm_medium) touch.utm_medium = legacy.utm_medium;
    if (legacy.utm_campaign) touch.utm_campaign = legacy.utm_campaign;
    if (legacy.gclid) touch.gclid = legacy.gclid; // legacy merged gclid/gbraid/wbraid
    return touch;
  } catch {
    return null;
  }
}

function generateRefCode(): string {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  const buf = new Uint8Array(5);
  try {
    window.crypto.getRandomValues(buf);
  } catch {
    for (let i = 0; i < buf.length; i++) buf[i] = Math.floor(Math.random() * 256);
  }
  let out = "";
  for (let i = 0; i < buf.length; i++) out += chars[buf[i] % chars.length];
  return `${REF_PREFIX}-${out}`;
}

/**
 * Runs once at boot (main.tsx, before React mounts). Also re-persists to the
 * 90-day cookie the moment the banner is accepted — before that the record only
 * lives in sessionStorage.
 */
export function captureAttribution(): void {
  if (typeof window === "undefined") return;
  try {
    let a = load();
    let isNewVisit = true;
    try {
      isNewVisit = !sessionStorage.getItem(VISIT_MARK);
      sessionStorage.setItem(VISIT_MARK, "1");
    } catch {
      /* private mode: treat every load as a visit */
    }
    const now = currentTouch();
    // Any fresh campaign signal on the URL / an external referrer marks a real
    // inbound touch; without one, a load whose referrer is this same site is a
    // CONTINUATION (ctrl+click / long-press opened a new tab — sessionStorage
    // is per-tab, so VISIT_MARK alone would misread it as a new direct visit
    // and wipe a tagged last touch with an empty one).
    const hasSignals =
      Boolean(now.referrer) ||
      UTM_KEYS.some((k) => now[k]) ||
      CLICK_ID_KEYS.some((k) => now[k]);
    let internalContinuation = false;
    if (!hasSignals) {
      try {
        internalContinuation =
          Boolean(document.referrer) &&
          new URL(document.referrer).hostname === window.location.hostname;
      } catch {
        /* ignore */
      }
    }
    if (!a) {
      a = { v: 2, ref_code: generateRefCode(), first: legacyFirstTouch() ?? now, last: now };
    } else if (hasSignals && (UTM_KEYS.some((k) => now[k]) || CLICK_ID_KEYS.some((k) => now[k]))) {
      // A new tagged click (fresh gclid/utm) refreshes the last touch even
      // mid-session — the same tab can carry two different ad clicks.
      a.last = now;
    } else if (isNewVisit && !internalContinuation) {
      a.last = now;
    }
    cached = a;
    persist(a);
    window.addEventListener(CONSENT_EVENT, () => {
      if (cached) persist(cached);
    });
  } catch {
    /* storage unavailable — attribution silently skipped */
  }
}

function getAttribution(): AttributionV2 | null {
  return cached ?? (cached = load());
}

/* ------------------------------------------------------------ classifiers */

/** CRM bucket (traffic_source) — same taxonomy as the 24.07 mechanism. */
function classifySource(t: Touch): string {
  const medium = (t.utm_medium || "").toLowerCase();
  const source = (t.utm_source || "").toLowerCase();
  if (t.gclid || t.gbraid || t.wbraid) return "google_ads";
  if (source === "google" && ["cpc", "ppc", "paid"].includes(medium)) return "google_ads";
  if (t.fbclid || (["facebook", "instagram", "meta"].includes(source) && medium.startsWith("paid"))) return "meta_ads";
  if (t.msclkid) return "paid_other";
  if (["cpc", "ppc", "paid", "paid_social", "display"].includes(medium)) return "paid_other";
  if (medium === "email" || source === "newsletter") return "email";
  // Переход из соцсети без признаков оплаты — это «social», а не «campaign».
  // Иначе размеченная ссылка в шапке Instagram (utm_medium=social) попадала бы
  // в тот же ящик, что и переходы от AI-ассистентов, и в карточке CRM вместо
  // понятного источника стояло бы бессмысленное «campaign» (BTQ-71).
  if (medium === "social" || SOCIAL_SOURCES.includes(source)) return "social";
  if (source) return "campaign";
  if (!t.referrer) return "direct";
  try {
    const host = new URL(t.referrer).hostname;
    if (/(^|\.)google\.|(^|\.)bing\.|(^|\.)duckduckgo\.|(^|\.)yandex\.|(^|\.)ecosia\.|(^|\.)qwant\./.test(host)) return "organic_search";
    if (/chatgpt\.com|openai\.com|perplexity\.ai|claude\.ai|gemini\.google|copilot\.microsoft/.test(host)) return "ai_search";
    if (/facebook\.|instagram\.|linkedin\.|pinterest\.|t\.co$|twitter\.|(^|\.)x\.com|youtube\.|tiktok\./.test(host)) return "social";
    return "referral";
  } catch {
    return "referral";
  }
}

const SEARCH_HOSTS =
  /(^|\.)google\.|(^|\.)bing\.|(^|\.)yahoo\.|(^|\.)duckduckgo\.|(^|\.)yandex\.|(^|\.)ecosia\.|(^|\.)qwant\.|(^|\.)baidu\.|(^|\.)seznam\.|(^|\.)search\.brave\./;
const AI_HOSTS =
  /chatgpt\.com|chat\.openai\.com|perplexity\.(ai|com)|claude\.ai|gemini\.google|bard\.google|copilot\.microsoft|(^|\.)you\.com|(^|\.)poe\.com|(^|\.)deepseek\.com|(^|\.)mistral\.ai/;
const SOCIAL_HOSTS =
  /facebook\.|instagram\.|linkedin\.|pinterest\.|t\.co$|twitter\.|(^|\.)x\.com|youtube\.|tiktok\.|(^|\.)reddit\.com|(^|\.)threads\.(net|com)|(^|\.)vk\.com|(^|\.)t\.me$|telegram\./;
const AI_SOURCES = ["chatgpt", "openai", "perplexity", "claude", "gemini", "copilot"];
const SOCIAL_SOURCES = ["facebook", "instagram", "linkedin", "tiktok", "twitter", "x", "pinterest", "youtube", "threads", "reddit", "telegram", "vk"];

/**
 * GA4 event dimension (spec 06.08, §6) — computed from the LAST touch, i.e. what
 * brought the current session. Precedence: paid → ai → organic → social →
 * referral → direct.
 */
export function trafficType(): TrafficType {
  const a = getAttribution();
  if (!a) return "direct";
  const t = a.last;
  const medium = (t.utm_medium || "").toLowerCase();
  const source = (t.utm_source || "").toLowerCase();
  if (t.gclid || t.gbraid || t.wbraid || t.fbclid || t.msclkid) return "paid";
  if (["cpc", "paid_social", "paid"].includes(medium)) return "paid";
  let host = "";
  try {
    host = t.referrer ? new URL(t.referrer).hostname : "";
  } catch {
    /* ignore */
  }
  if ((host && AI_HOSTS.test(host)) || AI_SOURCES.includes(source)) return "ai";
  if ((host && SEARCH_HOSTS.test(host) && !source && !medium) || medium === "organic") return "organic";
  if ((host && SOCIAL_HOSTS.test(host)) || medium === "social" || SOCIAL_SOURCES.includes(source)) return "social";
  if (host || source || t.utm_campaign) return "referral";
  return "direct";
}

/* --------------------------------------------------------------- outputs */

/** "EB-XXXXX", or "" when storage is unavailable. */
export function getRefCode(): string {
  const a = getAttribution();
  return a ? a.ref_code : "";
}

/** Fields spread into every lead-form payload (touch choice: see header). */
/**
 * First-touch referrer as origin + path (BTQ-88). Two reasons it is trimmed:
 * the query string of a referring URL can carry another site's parameters,
 * which are none of our business and occasionally personal, and the CRM field
 * is a plain text property. Until now the referrer only existed inside the
 * traffic_detail summary, which the edge function truncates at 500 chars — so
 * on long strings it was the first thing lost.
 */
function safeReferrer(raw?: string): string {
  if (!raw) return "";
  try {
    const u = new URL(raw);
    return `${u.origin}${u.pathname}`.slice(0, 300);
  } catch {
    return "";
  }
}

/**
 * Endpoint that stores "this ref code came from this source" (BTQ-88).
 * Both sites share it — the function lives in btq-api (Cloudflare Worker).
 */
const CAPTURE_REF_URL = fnUrl("capture-ref");

/**
 * Snapshots the current source next to the visitor ref code, so a WhatsApp
 * enquiry can be attributed later.
 *
 * A form lead carries its source in the payload. A WhatsApp lead carries only
 * the code in the text of the first message — until now nothing stored what
 * that code meant, so those enquiries landed in the CRM with no origin at all.
 *
 * Fired on the click that leaves for the messenger, hence sendBeacon: it
 * survives the page going away, and it cannot delay the navigation. Failure is
 * silent on purpose — losing this costs one unattributed enquiry, never the
 * enquiry itself.
 */
export function captureRefSnapshot(): void {
  try {
    const a = getAttribution();
    if (!a?.ref_code) return;
    const f = trafficFields();
    const body = JSON.stringify({
      ref_code: a.ref_code,
      traffic_source: f.traffic_source,
      traffic_detail: f.traffic_detail,
      landing_page: f.landing_page,
      referrer: f.referrer,
      utm_source: f.utm_source,
      utm_medium: f.utm_medium,
      utm_campaign: f.utm_campaign,
      utm_content: f.utm_content,
      utm_term: f.utm_term,
      gclid: f.gclid,
    });
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon(CAPTURE_REF_URL, new Blob([body], { type: "application/json" }));
      return;
    }
    void fetch(CAPTURE_REF_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* attribution is best-effort, never block the click */
  }
}

export function trafficFields(): Record<string, string> {
  const a = getAttribution();
  if (!a) return { traffic_source: "unknown", traffic_detail: "" };
  const first = a.first;
  const last = a.last;

  // CRM «Доп. источники» string — first-touch, same shape as since 24.07. The
  // ref code goes FIRST: the shared edge function truncates traffic_detail to
  // 500 chars, and appended at the end the code would be exactly the part cut
  // off on long strings.
  const bits: string[] = [`ref-code: ${a.ref_code}`];
  if (first.utm_campaign) bits.push(`campaign: ${first.utm_campaign}`);
  else if (first.utm_source) bits.push(`${first.utm_source}/${first.utm_medium || "?"}`);
  if (first.referrer) {
    try {
      bits.push(`ref: ${new URL(first.referrer).hostname}`);
    } catch {
      /* ignore */
    }
  }
  if (first.landing) bits.push(`landing: ${first.landing}`);
  if (first.ts) bits.push(`first seen: ${first.ts.slice(0, 10)}`);

  // Discrete fields come from ONE touch — the last when the current visit is
  // tagged (any utm or click id), else the first. Mixing touches would pair a
  // fresh gclid with an old campaign's utm labels.
  const tagged = UTM_KEYS.some((k) => last[k]) || CLICK_ID_KEYS.some((k) => last[k]);
  const src = tagged ? last : first;
  const googleClick = src.gclid || src.gbraid || src.wbraid || "";

  return {
    traffic_source: classifySource(first),
    traffic_detail: bits.join(" · "),
    lead_ref: a.ref_code,
    gclid: googleClick,
    utm_source: src.utm_source || "",
    utm_medium: src.utm_medium || "",
    utm_campaign: src.utm_campaign || "",
    utm_content: src.utm_content || "",
    utm_term: src.utm_term || "",
    landing_page: last.landing || "",
    referrer: safeReferrer(first.referrer),
  };
}

/**
 * %20-space, %27-apostrophe percent-encoding for wa.me text values — matches
 * the spec's literal strings; encodeURIComponent alone leaves ' raw.
 */
function encodeWaText(text: string): string {
  // split/join, not a /'/ regex literal: tools/tsxcheck.py's scanner reads the
  // apostrophe in a regex as a string opener and misparses the rest of the file.
  return encodeURIComponent(text).split("'").join("%27");
}

/**
 * Short page bucket stitched into the contact tag (BTQ-117) so the manager sees
 * WHERE the person reached out from in the first WhatsApp message, without
 * opening the CRM. Buckets differ from the BTQ twin's: this site has no /lp/*
 * ad landings, and it does have /services/*.
 *
 * Read at CLICK time from the current path, deliberately NOT from the stored
 * first touch: the useful answer is "which page was he on when he wrote", and
 * that stays correct for a visitor whose ref code was minted weeks earlier on
 * some other page. The stored first touch already lives in traffic_detail.
 *
 * Kept OUT of the ref code itself: the code is frozen at the first visit and is
 * already sitting in CRM cards and in message history, so changing its shape
 * would strand every existing one.
 */
function landingTag(): string {
  let path = "";
  try {
    path = window.location.pathname.toLowerCase();
  } catch {
    return "SITE";
  }
  // Trailing slashes are cosmetic here; EB canonicalises WITH one, so "/venues/"
  // and "/venues" have to land in the same bucket.
  path = path.replace(/\/+$/, "");
  if (!path) return "HOME";
  if (path.startsWith("/venues")) return "VEN";
  if (path.startsWith("/locations")) return "LOC";
  if (path.startsWith("/services")) return "SVC";
  if (path.startsWith("/blog")) return "BLOG";
  if (path.startsWith("/contact")) return "CONTACT";
  return "SITE";
}

/** "[ref:EB-034AJ · VEN]" — who the visitor is, plus where he wrote from. */
function contactTag(code: string): string {
  return `[ref:${code} · ${landingTag()}]`;
}

/** wa.me href with the given prefilled text plus the ref code. */
export function waHref(text: string): string {
  const code = getRefCode();
  const suffix = code ? ` ${contactTag(code)}` : "";
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeWaText(text + suffix)}`;
}

/**
 * Removes the per-visitor [ref:XXX-XXXXX · BUCKET] tag (plain or percent-encoded)
 * from a href so GA4's link_url dimension aggregates instead of splintering into
 * one value per visitor.
 *
 * The bucket half is optional in the pattern on purpose: links carrying the old
 * bare [ref:CODE] shape still exist in prerendered HTML and in whatever a
 * visitor has cached, and both must keep collapsing to the same link_url. If
 * this pattern ever stops matching the tag that waHref writes, nothing throws —
 * GA4 just quietly grows one link_url per visitor and the WhatsApp button's
 * stats become unreadable. Change the two together.
 */
// new RegExp(string), not a literal: tools/tsxcheck.py has no regex state and
// reads the escaped \[ ... \] of a literal as an unbalanced bracket pair.
const REF_TAG_RE = new RegExp(
  "(?:%20|\\+| )?(?:\\[ref:|%5Bref%3A)[A-Z]+-[A-Z0-9]{5}" +
    "(?:(?:%20|\\+| )(?:%C2%B7|·)(?:%20|\\+| )[A-Z-]+)?" +
    "(?:\\]|%5D)",
  "g",
);

export function stripRefTag(href: string): string {
  return href.replace(REF_TAG_RE, "");
}

/**
 * Click-time ref injection for arbitrary wa.me / mailto links (analytics.ts
 * calls this from the delegated click handler, so links authored anywhere —
 * including prerendered HTML — carry the code without per-component wiring).
 * Never touches a link that already carries a [ref:] tag.
 */
export function enrichContactHref(href: string, method: "whatsapp" | "email" | "phone"): string {
  const code = getRefCode();
  if (!code || href.includes("[ref:") || href.includes(encodeURIComponent("[ref:"))) return href;
  const tag = contactTag(code);
  try {
    if (method === "whatsapp") {
      // Not searchParams.set(): that serialises spaces as "+" (form encoding),
      // which some WhatsApp clients show literally. Build the text param by
      // hand with %20 encoding instead.
      const url = new URL(href, window.location.href);
      const existing = url.searchParams.get("text");
      url.searchParams.delete("text");
      const text = existing ? `${existing} ${tag}` : `${DEFAULT_WA_TEXT} ${tag}`;
      const base = url.toString();
      return `${base}${base.includes("?") ? "&" : "?"}text=${encodeWaText(text)}`;
    }
    if (method === "email") {
      if (/[?&]subject=/i.test(href)) {
        return href.replace(/([?&]subject=[^&]*)/i, (m) => `${m}${encodeURIComponent(` ${tag}`)}`);
      }
      const sep = href.includes("?") ? "&" : "?";
      return `${href}${sep}subject=${encodeURIComponent(`Enquiry ${tag}`)}`;
    }
  } catch {
    /* malformed href — leave as authored */
  }
  return href;
}
